'use client';

import { useUser } from '@auth0/nextjs-auth0';
import { useState } from 'react';
import { PENDING_SAVE_TRIP_KEY, type PendingSaveTrip } from '~/constants/savedTrips';
import { TRPC_SETTINGS } from '~/constants/trpc';
import { api } from '~/libs/api';
import { useSharedRouter } from '~/utils/nextMigration/router';

export const SAVE_TRIP_TRACKING_ID = 'savetrip-button';

interface UseSaveTripArgs {
  tripId?: string;
  startDate?: string;
  endDate?: string;
}

export interface SaveTripOverride {
  tripId?: string;
  tripPageUrl?: string;
  startDate?: string;
  endDate?: string;
}

export function useSaveTrip({ tripId, startDate, endDate }: UseSaveTripArgs = {}) {
  const { user } = useUser();
  const isLoggedIn = !!user;
  const router = useSharedRouter();
  const utils = api.useUtils();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeTripId, setActiveTripId] = useState<string | undefined>(tripId);

  const saveTrip = api.account.saveTrip.useMutation();
  const removeTrip = api.account.removeTrip.useMutation();

  const { data: isTripSaved } = api.account.isTripSaved.useQuery(
    { tripId: tripId ?? '' },
    {
      enabled: isLoggedIn && Boolean(tripId),
      ...TRPC_SETTINGS.QUERY_NO_REFETCH
    }
  );

  async function toggleSave(override?: SaveTripOverride) {
    const effectiveTripId = override?.tripId ?? tripId;
    if (!effectiveTripId) return;

    setActiveTripId(effectiveTripId);

    if (!isLoggedIn) {
      setIsDialogOpen(true);
      return;
    }

    if (isTripSaved) {
      await removeTrip.mutateAsync({ tripId: effectiveTripId });
    } else {
      await saveTrip.mutateAsync({
        tripId: effectiveTripId,
        notify: true,
        tripPageUrl: override?.tripPageUrl ?? (globalThis.window === undefined ? '' : globalThis.location.href),
        startDate: override?.startDate ?? startDate,
        endDate: override?.endDate ?? endDate
      });
    }

    await utils.account.isTripSaved.invalidate({ tripId: effectiveTripId });
    void utils.account.savedTrips.invalidate();
    setIsDialogOpen(false);
  }

  function goToLogin() {
    const loginTripId = activeTripId ?? tripId;
    if (!loginTripId) return;

    const pending: PendingSaveTrip = {
      tripId: loginTripId,
      tripPageUrl: globalThis.location?.href ?? '',
      startDate,
      endDate
    };
    globalThis.sessionStorage?.setItem(PENDING_SAVE_TRIP_KEY, JSON.stringify(pending));
    router.push(`/auth/login?returnTo=/account?tripId=${loginTripId}`);
  }

  return {
    isLoggedIn,
    isTripSaved: !!isTripSaved,
    isDialogOpen,
    setIsDialogOpen,
    toggleSave,
    goToLogin,
    goToAccount: () => router.push('/account')
  };
}
